/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
	sessionIsAipAdmin,
	sessionStrictIsSurveyLoader
} from '../../../../../../../util/user';

import { thunkDelete } from '../../../../../../redux/LinesProjectsReports/slices/LinesAggs/actions';
import {
	selectIsDeletingThisLineAgg,
	selectIsDeletedThisLineAgg,
	selectErrorMessageForDeletingThisLineAgg
} from '../../../../../../redux/LinesProjectsReports/slices/LinesAggs/selectors';

import { AimIconButton } from '../../../../../../../commons/components/AimButtons';
import ModalForceDelete from './components/ModalForceDelete';
import ModalConfirmUndo from '../../../../../ConfirmUndo';

import { fetchLineHistorySummaries } from './api';

import './ButtonDelete.scss';

const IS_AIP_ADMIN = sessionIsAipAdmin();
const IS_SURVEY_LOADER = sessionStrictIsSurveyLoader(); // only returns true for customer accounts that have survey loader permissions

const userCanDelete = allJobsUserCreated => {
	return IS_AIP_ADMIN || (IS_SURVEY_LOADER && allJobsUserCreated);
};

const jobHasUserData = job => {
	const props = ['line_state_count', 'report_state_count'];
	return (
		!!props.filter(key => {
			const val = job[key] || 0;
			return val > 0;
		}).length > 0
	);
};

const mapStateToProps = (state, ownProps) => {
	const { item } = ownProps;
	return {
		isDeleting: selectIsDeletingThisLineAgg(state, item?.id),
		isDeleted: selectIsDeletedThisLineAgg(state, item?.id),
		errorMessage: selectErrorMessageForDeletingThisLineAgg(state, item?.id)
	};
};

const mapDispatchToProps = dispatch => {
	const actionCreators = {
		deleteItem: thunkDelete
	};
	return bindActionCreators(actionCreators, dispatch);
};

const ButtonDelete = ({
	item,
	isDeleting,
	isDeleted,
	errorMessage,
	deleteItem
} = {}) => {
	const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
	const [showQuickDeleteModal, setShowQuickDeleteModal] = useState(false);
	const [historyDetails, setHistoryDetails] = useState();
	const [isLoadingHistoryDetails, setIsLoadingHistoryDetails] = useState(false);

	const hasUserData = useMemo(() => {
		const { jobs = [] } = item;
		return !!jobs.filter(j => jobHasUserData(j)).length > 0;
	}, [item]);

	const allJobsUserCreated = useMemo(() => {
		const { jobs = [] } = item;
		return !!jobs.every(j => j.is_created_by_customer);
	}, [item]);

	const { disabled, bsStyle, iconClassName, title } = useMemo(() => {
		const errorStyle = errorMessage ? 'danger' : undefined;
		const regularStyle = isDeleted ? 'default' : 'primary';
		return {
			disabled: isDeleted || isDeleting,
			bsStyle: errorStyle || regularStyle,
			iconClassName: isDeleting ? 'fa fa-spinner fa-spin' : 'fa fa-trash',
			title: errorMessage || undefined
		};
	}, [isDeleting, isDeleted, errorMessage]);

	const onClickDelete = useCallback(() => {
		if (hasUserData) {
			setIsLoadingHistoryDetails(true);
			setShowForceDeleteModal(true);
			fetchLineHistorySummaries({ lineId: item.lineId }).then(res => {
				setHistoryDetails(res);
				setIsLoadingHistoryDetails(false);
			});
		} else {
			setShowQuickDeleteModal(true);
		}
	}, [item, deleteItem, hasUserData]);

	const onClickConfirmQuickDelete = useCallback(() => {
		deleteItem(item);
	}, [deleteItem, item]);

	const fnClose = useCallback(() => {
		setShowForceDeleteModal(false);
		setShowQuickDeleteModal(false);
	}, []);

	const fnCloseOnEscape = useCallback(
		e => {
			if (e.key === 'Escape' || e.code === 'Escape') {
				fnClose();
			}
		},
		[fnClose]
	);

	useEffect(() => {
		const hasError = errorMessage && errorMessage.length;
		if (showForceDeleteModal && (hasError || isDeleted)) {
			setShowForceDeleteModal(false);
		}
	}, [showForceDeleteModal, errorMessage, isDeleted]);

	if (!userCanDelete(allJobsUserCreated)) {
		return null;
	}

	return (
		<div className="btn-linesaggs-delete-wrapper" onKeyDown={fnCloseOnEscape}>
			<AimIconButton
				className="outline"
				disabled={disabled}
				iconClassname={iconClassName}
				onClick={onClickDelete}
				bsStyle={bsStyle}
				title={title}
			/>
			{showForceDeleteModal && (
				<ModalForceDelete
					item={item}
					show={showForceDeleteModal}
					close={fnClose}
					isLoading={isLoadingHistoryDetails}
					historyDetails={historyDetails}
					isDeleting={isDeleting}
					deleteItem={deleteItem}
				/>
			)}
			{showQuickDeleteModal && (
				<ModalConfirmUndo
					title="Confirm Delete?"
					isOpen={showQuickDeleteModal}
					buttonTextConfirm="Confirm Delete"
					buttonBsStyleConfirm="warning"
					onClose={fnClose}
					onCancel={fnClose}
					onConfirm={onClickConfirmQuickDelete}
				>
					You are about to delete the below:
					<h3>
						<span style={{ opacity: 0.4 }}>Line:</span>{' '}
						<span style={{ color: 'rgb(14, 165, 233)' }}>{item.lineName}</span>
					</h3>
					{(item.jobs || []).map(j => (
						<div>
							<span style={{ opacity: 0.4 }}>Surveys:</span>&nbsp;
							<span style={{ color: 'rgb(249, 115, 22)' }}>
								{(j.surveys || []).map(s => s.type).join(', ')}
							</span>
							&nbsp;
							<span style={{ opacity: 0.4 }}>Job #:</span>&nbsp;
							<span style={{ color: '#555' }}>{j.jobNumber}</span>&nbsp;
						</div>
					))}
				</ModalConfirmUndo>
			)}
		</div>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ButtonDelete);
